/**
 * /dila/:handle — D.I.L.A. "build-your-outfit" clothing rack.
 *
 * The whole DILA collection is loaded with full variant data so a customer can
 * see every core piece (Set / Hoodie / Lux Tee / Cropped Sweats) in a chosen
 * colorway ("wash"), pick a size for each, and add the lot to a Shopify-connected
 * cart in one fell swoop — without bouncing in and out of product pages.
 *
 * Sun-faded patina aesthetic. Grain overlay kept; muddy blend removed from photos.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Loader2, Clock, ShoppingBag, X, Check, Plus, Bell } from 'lucide-react';
import api, { subscribeNewsletter } from '../services/api';
import { useCart } from '../context/CartContext';
import DilaFooter from '../components/DilaFooter';
import { toast } from 'sonner';

const TYPE_LABEL = { set: 'Sweatsuit Set', hoodie: 'Cropped Hoodie', tee: 'Lux Tee', sweats: 'Cropped Sweats', hat: 'Bucket Hat' };
const TYPE_ORDER = ['set', 'hoodie', 'tee', 'sweats', 'hat'];
const SIZE_ORDER = ['2T', '4T', '5-6 years', '6', '7-8 years', '8', '9-10 years', 'YXS', 'YS', 'YM', 'YL', 'YXL', 'XS', 'S', 'M', 'L', 'XL', '2XL', 'XXL', '3XL', 'XXXL'];
const sizeRank = (s) => { const i = SIZE_ORDER.indexOf(s); return i < 0 ? 999 : i; };

// The size option may be named "Size" or "Accessory size" (hats) — use any non-Color option.
const sizeKeyFor = (p) => {
  const o = (p.options || []).find((opt) => opt.name !== 'Color');
  return o ? o.name : 'Size';
};

const coreType = (p) => {
  const n = (p.name || '').toLowerCase();
  const cat = (p.category || '').toLowerCase();
  if (cat === 'accessories' || n.includes('hat') || n.includes('bucket') || n.includes('beanie') || n.includes('cap')) return 'hat';
  if (cat === 'suits' || n.includes('sweatsuit') || n.includes('set')) return 'set';
  if (n.includes('hoodie')) return 'hoodie';
  if (n.includes('tee')) return 'tee';
  if (n.includes('sweats') || cat === 'bottoms') return 'sweats';
  return 'other';
};

const DilaProduct = () => {
  const { handle } = useParams();
  const [searchParams] = useSearchParams();
  const { cart, addManyToCart, removeLineItem, goToCheckout, loading: cartLoading } = useCart();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [color, setColor] = useState(null);
  const [sizes, setSizes] = useState({});       // { type: sizeString }
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [discount, setDiscount] = useState({ code: null, percentage: 10 });
  const [notifyEmail, setNotifyEmail] = useState({});  // { type: email }
  const [notified, setNotified] = useState({});        // { type: true }

  const handleNotify = async (type) => {
    const em = (notifyEmail[type] || '').trim();
    if (!/^\S+@\S+\.\S+$/.test(em)) { toast.error('Enter a valid email'); return; }
    try {
      await subscribeNewsletter(em);
      setNotified((n) => ({ ...n, [type]: true }));
      toast.success("We'll email you the moment this wash drops.");
    } catch (e) {
      toast.error('Something went wrong. Try again.');
    }
  };

  useEffect(() => {
    Promise.all([
      api.get('/shopify/collections/dila-1/full').then((r) => r.data?.collection?.products || []).catch(() => []),
      api.get('/shopify/dila/discount').then((r) => r.data).catch(() => null),
    ]).then(([prods, disc]) => {
      setProducts(prods);
      if (disc?.code) setDiscount({ code: disc.code, percentage: disc.percentage });
      // Determine starting colorway: URL param > current product's color > first available
      const current = prods.find((p) => p.handle === handle);
      const colorsAll = Array.from(new Set(prods.flatMap((p) => p.variants.map((v) => v.options?.Color).filter(Boolean)))).sort();
      const urlColor = searchParams.get('color');
      let start = urlColor && colorsAll.includes(urlColor) ? urlColor : null;
      if (!start && current) {
        const cc = Array.from(new Set(current.variants.map((v) => v.options?.Color).filter(Boolean)));
        start = cc[0] || colorsAll[0];
      }
      setColor(start || colorsAll[0] || null);
    }).finally(() => setLoading(false));
  }, [handle, searchParams]);

  const availableColors = useMemo(
    () => Array.from(new Set(products.flatMap((p) => p.variants.map((v) => v.options?.Color).filter(Boolean)))).sort(),
    [products]
  );

  // Build the rack for the selected colorway: one card per core type available in that wash.
  const rack = useMemo(() => {
    if (!color) return [];
    const byType = {};
    products.forEach((p) => {
      const t = coreType(p);
      if (!TYPE_ORDER.includes(t) || byType[t]) return;
      const variants = p.variants.filter((v) => v.options?.Color === color);
      if (!variants.length) return;
      const sk = sizeKeyFor(p);
      byType[t] = {
        type: t,
        product: p,
        image: variants.find((v) => v.image)?.image || p.images?.[0]?.url || p.image,
        price: variants[0]?.price,
        sizes: variants
          .map((v) => ({ size: v.options?.[sk], variantId: v.id, available: v.available, price: v.price }))
          .filter((s) => s.size)
          .sort((a, b) => sizeRank(a.size) - sizeRank(b.size)),
      };
    });
    return TYPE_ORDER.filter((t) => byType[t]).map((t) => byType[t]);
  }, [products, color]);

  const current = products.find((p) => p.handle === handle);
  const focusType = current ? coreType(current) : null;

  // Reset size choices when colorway changes (variant ids differ per wash).
  useEffect(() => { setSizes({}); }, [color]);

  const variantFor = (item) => {
    const size = sizes[item.type];
    if (!size) return null;
    return item.sizes.find((s) => s.size === size);
  };

  const selectedItems = rack
    .map((item) => ({ item, v: variantFor(item) }))
    .filter(({ v }) => v && v.available);

  const cartCount = (cart?.lineItems || []).reduce((t, i) => t + i.quantity, 0);
  const cartSubtotal = (cart?.lineItems || []).reduce(
    (t, i) => t + parseFloat(i.variant?.price?.amount || 0) * i.quantity,
    0
  );

  // "Complete the Look" savings meter for the current colorway.
  const rate = (discount.percentage || 10) / 100;
  const rackInCart = (item) =>
    (cart?.lineItems || []).some((li) => item.sizes.some((s) => s.variantId === li.variantId));
  const missingPieces = rack.filter((item) => !rackInCart(item));
  const nextPiece = missingPieces[0] || null;
  const savedNow = cartCount >= 2 ? cartSubtotal * rate : 0;
  const projectedSubtotal = cartSubtotal + (nextPiece ? parseFloat(nextPiece.price || 0) : 0);
  const projectedSaved = cartCount + 1 >= 2 ? projectedSubtotal * rate : 0;
  const extraSaving = Math.max(projectedSaved - savedNow, 0);
  const outfitComplete = cartCount > 0 && missingPieces.length === 0;

  const handleAddOne = async (item) => {
    const v = variantFor(item);
    if (!v) { toast.error('Pick a size first'); return; }
    // Stay on the rack so the customer can keep stacking pieces (no drawer churn).
    await addManyToCart([{ variantId: v.variantId, quantity: 1 }]);
  };

  const handleAddAll = async () => {
    if (selectedItems.length === 0) { toast.error('Choose a size on at least one piece'); return; }
    const ok = await addManyToCart(selectedItems.map(({ v }) => ({ variantId: v.variantId, quantity: 1 })));
    if (ok) { setSizes({}); setDrawerOpen(true); }
  };

  const handleCheckout = () => {
    goToCheckout(cartCount >= 2 ? discount.code : null);
  };

  return (
    <div
      className="min-h-screen text-stone-900"
      style={{
        background:
          'radial-gradient(circle at 25% 15%, #f7dfa9 0%, transparent 50%), radial-gradient(circle at 75% 75%, #c87f4a 0%, transparent 60%), linear-gradient(135deg, #f3e6cd 0%, #e0b780 50%, #a86838 100%)',
      }}
      data-testid="dila-product-page"
    >
      {/* Grain overlay (kept) */}
      <div
        className="fixed inset-0 pointer-events-none opacity-25 mix-blend-multiply z-0"
        style={{ backgroundImage: 'repeating-linear-gradient(40deg, transparent 0 10px, rgba(0,0,0,0.04) 10px 11px)' }}
      />

      {/* Floating cart button */}
      <button
        onClick={() => setDrawerOpen(true)}
        className="fixed top-5 right-5 z-40 flex items-center gap-2 bg-stone-900 text-amber-50 px-4 py-2.5 shadow-lg hover:bg-stone-800 transition-colors"
        data-testid="dila-cart-button"
      >
        <ShoppingBag className="w-4 h-4" />
        <span className="text-[11px] font-black tracking-[0.2em] uppercase">Rack</span>
        {cartCount > 0 && (
          <span className="bg-amber-50 text-stone-900 w-5 h-5 rounded-full text-[11px] font-black flex items-center justify-center">
            {cartCount}
          </span>
        )}
      </button>

      <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        {/* Crumb */}
        <div className="flex items-center justify-between mb-10">
          <Link to="/matrix" className="text-[10px] tracking-[0.35em] uppercase font-black hover:opacity-60 flex items-center gap-2">
            <ArrowLeft className="w-3 h-3" /> PRSVR · Matrix
          </Link>
          <Link to="/dila" className="text-[10px] tracking-[0.35em] uppercase font-black hover:opacity-60">
            Design House
          </Link>
        </div>

        {loading ? (
          <div className="py-32 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin" />
          </div>
        ) : rack.length === 0 ? (
          <div className="py-32 text-center">
            <h2 className="text-3xl font-black uppercase mb-4">This wash is resting.</h2>
            <Link to="/dila" className="underline text-sm uppercase tracking-widest">Return to the collection</Link>
          </div>
        ) : (
          <>
            {/* Title */}
            <div className="text-[10px] tracking-[0.4em] uppercase font-black mb-4 opacity-70">
              D.I.L.A. · Slow-Cured Apparel {focusType && TYPE_LABEL[focusType] ? `· ${TYPE_LABEL[focusType]}` : ''}
            </div>
            <h1 className="text-5xl sm:text-7xl lg:text-[9rem] font-black uppercase leading-[0.85] tracking-tight mb-6" data-testid="dila-wash-heading">
              Wash <span className="italic opacity-70">{color}.</span>
            </h1>
            <p className="text-base sm:text-lg max-w-2xl leading-relaxed mb-8 font-medium">
              Reserve your pieces below. Mix and match this colorway like a rack — pick your sizes,
              stack the fit, and reserve it all at once. Add a second piece and{' '}
              <span className="font-black underline">save {Math.round(discount.percentage)}%</span> on the set —
              the wash only runs once.
            </p>

            {/* Complete the Look — savings meter */}
            {cartCount > 0 && (
              <div
                className="flex items-center gap-3 border-2 border-stone-900 bg-stone-900 text-amber-50 px-5 py-3.5 mb-10 max-w-2xl"
                data-testid="dila-savings-meter"
              >
                <ShoppingBag className="w-4 h-4 shrink-0" />
                <div className="text-[11px] sm:text-xs tracking-[0.12em] uppercase font-black leading-snug">
                  {outfitComplete ? (
                    <>The full {color} look is on your rack — {Math.round(discount.percentage)}% off applied at checkout.</>
                  ) : cartCount >= 2 ? (
                    <>
                      You've saved ${savedNow.toFixed(2)} ({Math.round(discount.percentage)}%).
                      {nextPiece && <> Add the {TYPE_LABEL[nextPiece.type]} to save ${extraSaving.toFixed(2)} more.</>}
                    </>
                  ) : (
                    <>
                      {nextPiece
                        ? <>Add the {TYPE_LABEL[nextPiece.type]} to unlock {Math.round(discount.percentage)}% — you'll save ${projectedSaved.toFixed(2)} on the outfit.</>
                        : <>Stack a second piece to unlock {Math.round(discount.percentage)}% off your outfit.</>}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Colorway selector */}
            {availableColors.length > 1 && (
              <div className="mb-12">
                <div className="text-[10px] tracking-[0.3em] uppercase font-black mb-3 opacity-70">Choose Your Wash</div>
                <div className="flex flex-wrap gap-3">
                  {availableColors.map((c) => (
                    <button
                      key={c}
                      onClick={() => setColor(c)}
                      className={`px-5 py-3 border-2 text-xs font-black tracking-[0.2em] uppercase transition-colors ${
                        color === c
                          ? 'border-stone-900 bg-stone-900 text-amber-50'
                          : 'border-stone-900/40 bg-white/30 hover:border-stone-900'
                      }`}
                      data-testid={`dila-colorway-${c}`}
                    >
                      Wash {c}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* The Rack */}
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 lg:gap-5 mb-10">
              {rack.map((item) => {
                const chosen = sizes[item.type];
                const v = variantFor(item);
                const inCart = v && (cart?.lineItems || []).some((li) => li.variantId === v.variantId);
                const tags = (item.product.tags || []).map((t) => String(t).toLowerCase());
                const isPreorder = tags.some((t) => t.includes('preorder') || t.includes('pre-order'));
                const hasAvailable = item.sizes.some((s) => s.available);
                const notifyState = !hasAvailable; // sold out / coming soon => capture interest
                return (
                  <div
                    key={item.type}
                    className={`bg-white/40 backdrop-blur border-2 ${focusType === item.type ? 'border-stone-900' : 'border-stone-900/30'} flex flex-col`}
                    data-testid={`dila-rack-item-${item.type}`}
                  >
                    <div className="relative overflow-hidden bg-stone-200">
                      {item.image && (
                        <Link to={`/dila/item/${item.product.handle}`} data-testid={`dila-rack-open-${item.type}`}>
                          <img src={item.image} alt={item.product.name} className="w-full h-auto block cursor-pointer hover:opacity-90 transition-opacity" />
                        </Link>
                      )}
                      {focusType === item.type && (
                        <div className="absolute top-2 left-2 bg-stone-900 text-amber-50 text-[9px] tracking-[0.2em] font-black px-2 py-1">
                          VIEWING
                        </div>
                      )}
                      {isPreorder ? (
                        <div className="absolute top-2 right-2 bg-amber-500 text-stone-900 text-[9px] tracking-[0.2em] font-black px-2 py-1">
                          PREORDER
                        </div>
                      ) : notifyState ? (
                        <div className="absolute top-2 right-2 bg-stone-900 text-amber-50 text-[9px] tracking-[0.2em] font-black px-2 py-1">
                          COMING SOON
                        </div>
                      ) : null}
                    </div>
                    <div className="p-4 flex flex-col flex-1">
                      <div className="text-sm font-black uppercase tracking-tight leading-tight">{TYPE_LABEL[item.type]}</div>
                      <div className="text-[10px] tracking-[0.25em] uppercase opacity-60 mt-1 mb-1">
                        Wash {color} · ${parseFloat(item.price || 0).toFixed(0)}
                      </div>
                      <Link
                        to={`/dila/item/${item.product.handle}`}
                        className="text-[10px] tracking-[0.15em] uppercase font-black underline underline-offset-2 opacity-70 hover:opacity-100 mb-3 inline-flex items-center gap-1"
                        data-testid={`dila-rack-details-${item.type}`}
                      >
                        View Details <ArrowRight className="w-3 h-3" />
                      </Link>

                      {notifyState ? (
                        notified[item.type] ? (
                          <div className="mt-auto flex items-center gap-2 border-2 border-stone-900 bg-stone-900 text-amber-50 px-3 py-2.5 text-[10px] font-black tracking-[0.15em] uppercase" data-testid={`dila-notified-${item.type}`}>
                            <Check className="w-3.5 h-3.5" /> On the list
                          </div>
                        ) : (
                          <div className="mt-auto space-y-2">
                            <input
                              type="email"
                              value={notifyEmail[item.type] || ''}
                              onChange={(e) => setNotifyEmail((m) => ({ ...m, [item.type]: e.target.value }))}
                              placeholder="you@email.com"
                              className="w-full border-2 border-stone-900/40 bg-white/70 px-3 py-2.5 text-xs font-medium placeholder:text-stone-900/40 focus:border-stone-900 outline-none"
                              data-testid={`dila-notify-email-${item.type}`}
                            />
                            <button
                              onClick={() => handleNotify(item.type)}
                              className="w-full flex items-center justify-center gap-2 bg-stone-900 text-amber-50 px-3 py-2.5 text-[11px] font-black tracking-[0.2em] uppercase hover:bg-stone-800 transition-colors"
                              data-testid={`dila-notify-${item.type}`}
                            >
                              <Bell className="w-3.5 h-3.5" /> Notify Me
                            </button>
                          </div>
                        )
                      ) : (
                        <>
                          <select
                            value={chosen || ''}
                            onChange={(e) => setSizes((s) => ({ ...s, [item.type]: e.target.value }))}
                            className="w-full border-2 border-stone-900/40 bg-white/70 px-3 py-2.5 text-xs font-bold uppercase tracking-wider focus:border-stone-900 outline-none mb-3"
                            data-testid={`dila-size-${item.type}`}
                          >
                            <option value="">Select size</option>
                            {item.sizes.map((s) => (
                              <option key={s.variantId} value={s.size} disabled={!s.available}>
                                {s.size}{!s.available ? ' — sold out' : ''}
                              </option>
                            ))}
                          </select>

                          <button
                            onClick={() => handleAddOne(item)}
                            disabled={!v || !v.available}
                            className="mt-auto flex items-center justify-center gap-2 border-2 border-stone-900 px-3 py-2.5 text-[11px] font-black tracking-[0.2em] uppercase transition-colors disabled:opacity-40 disabled:cursor-not-allowed hover:bg-stone-900 hover:text-amber-50"
                            data-testid={`dila-add-${item.type}`}
                          >
                            {inCart ? (<><Check className="w-3.5 h-3.5" /> Added</>) : isPreorder ? (<><Plus className="w-3.5 h-3.5" /> Preorder</>) : (<><Plus className="w-3.5 h-3.5" /> Add</>)}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Add-all bar */}
            <div className="sticky bottom-4 z-20">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 border-2 border-stone-900 bg-amber-50/90 backdrop-blur px-5 py-4 shadow-[0_20px_40px_-20px_rgba(80,40,15,0.6)]">
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4" />
                  <span className="text-[11px] tracking-[0.2em] uppercase font-black">
                    {selectedItems.length > 0
                      ? `${selectedItems.length} piece${selectedItems.length > 1 ? 's' : ''} sized`
                      : 'Pick sizes to build your fit'}
                    {selectedItems.length >= 2 ? ` · Save ${Math.round(discount.percentage)}%` : ''}
                  </span>
                </div>
                <button
                  onClick={handleAddAll}
                  disabled={selectedItems.length === 0 || cartLoading}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 bg-stone-900 text-amber-50 px-6 py-3 text-xs font-black tracking-[0.25em] uppercase transition-colors hover:bg-stone-800 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="dila-add-all"
                >
                  {cartLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
                  Reserve Selected
                </button>
              </div>
            </div>

            <p className="mt-12 text-xs italic leading-relaxed max-w-2xl opacity-70">
              Reservations open today. Pieces are hand-finished and rested in disciplined batches.
              Once cleared from the manifest, the run does not return.
            </p>

            <DilaFooter />
          </>
        )}
      </div>

      {/* Cart drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50" data-testid="dila-cart-drawer">
          <div className="absolute inset-0 bg-stone-900/50 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-amber-50 shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-6 py-5 border-b-2 border-stone-900">
              <div className="text-sm font-black tracking-[0.3em] uppercase">Your Rack</div>
              <button onClick={() => setDrawerOpen(false)} data-testid="dila-cart-close" className="hover:opacity-60">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {cartCount === 0 ? (
                <div className="py-20 text-center text-sm uppercase tracking-widest opacity-60">
                  Your rack is empty.
                </div>
              ) : (
                <div className="space-y-4">
                  {(cart?.lineItems || []).map((li, idx) => (
                    <div key={li.variantId || idx} className="flex gap-3 border-b border-stone-900/15 pb-4" data-testid={`dila-cart-line-${idx}`}>
                      <div className="w-16 h-20 bg-stone-200 overflow-hidden shrink-0">
                        {li.variant?.image?.url && (
                          <img src={li.variant.image.url} alt={li.title} className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-black uppercase tracking-tight leading-tight">{li.title}</div>
                        <div className="text-[10px] uppercase tracking-widest opacity-60 mt-1">{li.variant?.title}</div>
                        <div className="text-xs font-bold mt-1">
                          ${parseFloat(li.variant?.price?.amount || 0).toFixed(2)} × {li.quantity}
                        </div>
                      </div>
                      <button
                        onClick={() => removeLineItem(li.variantId)}
                        disabled={cartLoading}
                        className="text-stone-900/50 hover:text-stone-900 self-start"
                        data-testid={`dila-cart-remove-${idx}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {cartCount > 0 && (
              <div className="border-t-2 border-stone-900 px-6 py-5 space-y-3">
                <div className="flex justify-between text-sm font-bold">
                  <span className="uppercase tracking-widest">Subtotal</span>
                  <span>${cartSubtotal.toFixed(2)}</span>
                </div>
                {cartCount >= 2 ? (
                  <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-emerald-800">
                    <Check className="w-3.5 h-3.5" /> {Math.round(discount.percentage)}% outfit discount applied at checkout
                  </div>
                ) : (
                  <div className="text-xs uppercase tracking-wider opacity-70">
                    Add one more piece to save {Math.round(discount.percentage)}%
                  </div>
                )}
                <button
                  onClick={handleCheckout}
                  className="w-full flex items-center justify-center gap-2 bg-stone-900 text-amber-50 px-6 py-4 text-xs font-black tracking-[0.3em] uppercase hover:bg-stone-800 transition-colors"
                  data-testid="dila-checkout-btn"
                >
                  Checkout <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setDrawerOpen(false)}
                  className="w-full text-center text-[11px] font-black tracking-[0.25em] uppercase opacity-70 hover:opacity-100 py-1"
                >
                  Keep Building
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DilaProduct;
